import React, { useEffect, useState } from 'react'
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ViewBase } from '../../../../components/common/ViewBase'
import { reverseUrl } from '../../../../navigation/reverseUrl'
import { isUserAdminInSociety } from '../../../../helpers/society'
import { useAuthenticatedUserId } from '../../../../hooks/useAuthenticatedUserId'
import { IconChoices } from '../../../../components/common/Icon'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { useFacilityTabs } from './useFacilityTabs'
import { FacilityInformation } from '../../../../components/society/Facility/FacilityInformation'
import { FacilityCalendar } from '../../../../components/society/Facility/FacilityCalendar'
import { FacilityBookings } from '../../../../components/society/Facility/FacilityBookings'
import { RouteErrorView } from '../../../error/RouteErrorView'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { facilityQueries } from '../../../../api/facilities'

export enum FacilityViewTabs {
  CALENDAR = 0,
  INFORMATION = 1,
  BOOKINGS = 2,
}

export const FacilityView = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { facilityId } = useParams()
  const { society, isLoading: isLoadingSociety } = useCurrentSociety()
  const navigate = useNavigate()
  const userId = useAuthenticatedUserId() as string
  const location = useLocation()
  const { getTabUrl, getTabState } = useFacilityTabs()

  const { data: facilities, isLoading: isLoadingFacilities } = useQuery(
    facilityQueries.getFacilitiesPerSociety(society?._id)
  )

  const facility = facilities?.find((_facility) => _facility._id === facilityId)

  useDocumentTitle(
    reverseDocumentTitle('society:facilities-detail', {
      '{{ societyName }}': society?.name,
      '{{ facilityName }}': facility?.name,
    })
  )

  const [activeTab, setActiveTab] = useState<FacilityViewTabs>(
    getTabState(location.pathname)
  )

  // When a new activeTab is set, we change the navigation to that tab as well
  useEffect(() => {
    const url = getTabUrl(activeTab)
    if (location.pathname !== url) {
      navigate(url)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  // When a new location is set (e.g. backbutton) we change the activeTab as well
  useEffect(() => {
    setActiveTab(getTabState(location.pathname))
  }, [getTabState, location.pathname])

  const tabs = [
    {
      title: translate('facilityView.tabBar.calendar'),
    },
    {
      title: translate('facilityView.tabBar.information'),
    },
    {
      title: translate('facilityView.tabBar.bookings'),
    },
  ]

  if (!facility || !society) {
    return null
  }

  const isAdmin = isUserAdminInSociety(society, userId)

  const loading = isLoadingFacilities || isLoadingSociety

  return (
    <ViewBase
      backUrl={reverseUrl('society:facilities', { id: facility.societyId })}
      title={facility?.name}
      tabBar={{ tabs, setActiveTab, activeTab }}
      loading={loading}
      buttons={
        isAdmin
          ? [
              {
                label: translate('common.actions.edit'),
                icon: IconChoices.EDIT,
                onClick: () =>
                  navigate(
                    reverseUrl('society:facilities-update', {
                      id: facility.societyId,
                      facilityId: facility._id,
                    })
                  ),
              },
            ]
          : undefined
      }
    >
      <Routes>
        <Route
          path="calendar"
          element={<FacilityCalendar facility={facility} society={society} />}
          errorElement={<RouteErrorView />}
        />
        <Route
          path="information"
          element={<FacilityInformation facility={facility} />}
          errorElement={<RouteErrorView />}
        />
        <Route
          path="bookings"
          element={<FacilityBookings facility={facility} />}
          errorElement={<RouteErrorView />}
        />
      </Routes>
    </ViewBase>
  )
}
